import { useEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

/**
 * ADO #700: edit-time reason popover. Anchored on the field the operator
 * just modified, prompts for a short justification (≤140 chars).
 */
const MAX_REASON_LENGTH = 140;

export interface FieldReasonPopoverProps {
  /** Stable reasonKey from `fieldReasonKey()`. */
  readonly reasonKey: string;
  /** Human-readable label rendered in the popover header. */
  readonly label: string;
  /** Anchor element — typically rendered inline next to the modified field. */
  readonly anchor: React.ReactNode;
  /** Open state controlled by the parent. */
  readonly open: boolean;
  /** Existing draft text (preserved across dismissals). */
  readonly draft: string;
  /** Called when the operator types in the textarea. */
  onDraftChange(draft: string): void;
  /** Called when the operator confirms a non-empty reason. */
  onCommit(reason: string): void;
  /** Called when the operator dismisses (Skip / Esc / outside-click). */
  onDismiss(): void;
}

export function FieldReasonPopover(props: FieldReasonPopoverProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [localDraft, setLocalDraft] = useState(props.draft);

  useEffect(() => {
    setLocalDraft(props.draft);
  }, [props.draft, props.open]);

  useEffect(() => {
    if (!props.open) return;
    // Focus the textarea when the popover opens.
    const handle = window.setTimeout(() => textareaRef.current?.focus(), 0);
    return () => window.clearTimeout(handle);
  }, [props.open]);

  function handleChange(value: string): void {
    const next = value.slice(0, MAX_REASON_LENGTH);
    setLocalDraft(next);
    props.onDraftChange(next);
  }

  function handleSave(): void {
    const trimmed = localDraft.trim();
    if (trimmed.length === 0) return;
    props.onCommit(trimmed);
  }

  return (
    <Popover
      open={props.open}
      onOpenChange={(next) => {
        if (!next) props.onDismiss();
      }}
    >
      <PopoverTrigger asChild>{props.anchor}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-80 space-y-3 border bg-background p-3 shadow-lg"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            props.onDismiss();
          }
        }}
      >
        <div className="space-y-1">
          <p className="text-xs font-semibold text-foreground">Reason for change</p>
          <p className="text-xs text-muted-foreground">
            Tell us why {props.label} was edited. This becomes part of the feedback record.
          </p>
        </div>
        <Textarea
          ref={textareaRef}
          rows={2}
          maxLength={MAX_REASON_LENGTH}
          placeholder={`Why was ${props.label} changed?`}
          value={localDraft}
          onChange={(event) => handleChange(event.target.value)}
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">
            {localDraft.length}/{MAX_REASON_LENGTH}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={props.onDismiss}>
              Skip
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={localDraft.trim().length === 0}
            >
              Save reason
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
